/**
 * - Get the ENV
 * - Optionally override local ENV
 * @param {'qa' | 'dev' | undefined} overrideLocalEnv
 * @returns {{ env:'prod' | 'qa' | 'dev', isLocal: boolean }} An object containing the ENV and a status to check if the app is running locally.
 */

import { mobileLatestVersionProd, mobileLatestVersionQA } from "../../utils";
import { DEFAULT_COUNTRY } from "../../utils/utils";

const getENV = (overrideLocalEnv = "qa") => {
  const { hostname = "" } = window?.location;

  if (hostname.includes("localhost")) {
    return {
      env: overrideLocalEnv,
      subEnv: "qa4",
      subVersion: "04",
      isLocal: true,
      latestMObileVersion: mobileLatestVersionQA(),
    };
  }

  if (hostname.includes("dev")) {
    return {
      env: "dev",
      subEnv: "dev4",
      subVersion: "04",
      isLocal: false,
      latestMObileVersion: mobileLatestVersionQA(),
    };
  }

  if (hostname.includes("qa4")) {
    return {
      env: "qa",
      subEnv: "qa4",
      subVersion: "04",
      isLocal: false,
      latestMObileVersion: mobileLatestVersionQA(),
    };
  }

  if (hostname.includes("qa5")) {
    return {
      env: "qa",
      subEnv: "qa5",
      subVersion: "05",
      isLocal: false,
      latestMObileVersion: mobileLatestVersionQA(),
    };
  }

  return {
    env: "prod",
    isLocal: false,
    latestMObileVersion: mobileLatestVersionProd(),
  };
};

export const ENV = getENV();

export const MOBILE_LATEST_VERSION = ENV.latestMObileVersion;
export const BASE_URL = ENV.isLocal
  ? `https://c-reserve-${ENV.subEnv}.safetruckparking.com`
  : "";
export const API_BASE_URL = `${BASE_URL}/services/stpsm-ws`;
export const API_BASE_RESERVATIONS_URL = `${BASE_URL}/services/stpreservations-ws`;

//to-do fix solr PROD ENV
const safetruck_parking_config =
  ENV.env === "prod"
    ? `c_safetruck_parking`
    : `c_${ENV.env}${ENV.subVersion}_safetruck_parking`;

const AUTH_COPART_URL = "services/gauthstp/oauth/token";
const VEHICLE_DETAILS_UPLOAD_IMAGE_COPART_URL =
  ENV.env === "prod"
    ? "https://ids.copart.com"
    : `https://c-ids-${ENV.subEnv}.copart.com/v4/upload`;

const apiConfig = {
  loginUser: `${API_BASE_URL}/api/login`,
  loginByToken: `${AUTH_COPART_URL}`,
  createUser: `${API_BASE_URL}/api/users`,
  logoutUser: `${API_BASE_URL}/api/v1/user/logout`,
  signupUser: `${API_BASE_URL}/api/auth/signup`,
  authLoginUser: `${API_BASE_URL}/api/auth/login`,
  refreshToken: `${API_BASE_URL}/api/auth/refresh`,

  signupAuthToken: `${AUTH_COPART_URL}`,
  resendVerificationEmail: `${API_BASE_URL}/api/validations/email/send`,
  forgotPassword: `${API_BASE_URL}/api/auth/forgot-password`,
  findByEmail: `${API_BASE_URL}/api/users/findByEmail`,
  sendEmailForgotPassword: `${API_BASE_URL}/api/auth/forgot-password/sendEmail`,

  addVehicle: `${API_BASE_URL}/api/vehicles`,
  vehicleDetailsUploadImage: `${VEHICLE_DETAILS_UPLOAD_IMAGE_COPART_URL}`,
  getStateList: `${API_BASE_URL}/api/validations/statelist`,
  phoneValidationRequest: `${API_BASE_URL}/api/validations/phonevalidation/${DEFAULT_COUNTRY}/+1`,
  countryPhoneValidationRequest: `${API_BASE_URL}/api/validations/phonevalidation/${DEFAULT_COUNTRY}`,
  addressValidation: `${API_BASE_URL}/api/validations/addressvalidation`,
  userExistsbyemail: `${API_BASE_URL}/api/validations/userexistsbyemail`,
  verifyUser: `${API_BASE_URL}/api/validations/email/verify?`,
  editVehicle: `${API_BASE_URL}/api/vehicles`,

  getDriverList: `${API_BASE_URL}/api/users/ByCustomerId`,
  deleteDriver: `${API_BASE_URL}/api/users`,
  updateDriver: `${API_BASE_URL}/api/users`,
  promoteToOwner: `${API_BASE_URL}/api/users/`,

  inviteDriver: `${API_BASE_URL}/api/invite-driver`,
  operatorSignUp: `${API_BASE_URL}/api/auth/operator`,
  operatorFileUpload: `${API_BASE_URL}/api/auth/upload`,
  operatorFileUploadLoggedIn: `${API_BASE_URL}/api/users/driver`,
  ownerFileUpload: `${API_BASE_URL}/api/users/userDocsUpload`,
  g2CustomerOrUserSignup: `${API_BASE_URL}/api/auth/completeSignUp/`,

  getDocumentsOwner: `${API_BASE_URL}/api/users/`,
  getDocumentsOperator: `${API_BASE_URL}/api/users/`,

  getProfileDetails: `${API_BASE_URL}/api/users/`,
  updateProfile: `${API_BASE_URL}/api/users/profile/`,

  hereAboutUs: `${API_BASE_URL}/api/referral`,

  gatePin: `${BASE_URL}/services/stpsm-ws/customer-app/api/solrsearch/gate_pin_codes`,

  gatePinLocations: `${API_BASE_RESERVATIONS_URL}/v1/gate-access/facilitiesAvailable`,

  reservationApis: {
    locations: `${API_BASE_URL}/api/locations`,
    pricingList: `${API_BASE_RESERVATIONS_URL}/v1/pricing/location`,
    reservations: `${BASE_URL}/services/stpreservations-ws/v1/reservations`,
    reservationsDetails: `${BASE_URL}/services/stpreservations-ws/v1/reservations`,
    availabilitySummary: `${BASE_URL}/services/stpreservations-ws/v1/reservations/availabilitySummary`,
    availabilityThresholdSummary: `${BASE_URL}/services/stpreservations-ws/v1/reservations/availabilityThresholdSummary`,
    reservationsList: `${BASE_URL}/services/stpsm-ws/customer-app/api/solrsearch/reservations`,
    locationSolrList: `${BASE_URL}/services/stpsm-ws/customer-app/api/solrsearch/locations`,
    extendReservation: `${BASE_URL}/services/stpreservations-ws/v1/reservations/\${reservationId}/extension?numOfDays=\${numOfDays}`,
    cancelReservation: `${BASE_URL}/services/stpreservations-ws/v1/reservations/\${reservationId}/cancel?extension=\${extension}`,
    scheduleMoveOut: `${BASE_URL}/services/stpreservations-ws/v1/reservations/\${reservationId}/vacate`,
    calculatedPrice: `${API_BASE_RESERVATIONS_URL}/v1/pricing/calculate`,
    getPaymentToken: `${API_BASE_RESERVATIONS_URL}/v1/payment-gateway/client-token/\${customerId}`,
    makePayment: `${API_BASE_RESERVATIONS_URL}/v1/customer/\${customerId}/reservation/\${reservationId}/payment?autoPayEnabled=\${autoPayEnabled}&savePaymentMethod=\${savePaymentMethod}`,
    makeInvoicePayment: `${API_BASE_RESERVATIONS_URL}/v1/customer/\${customerId}/payment?autoPayEnabled=\${autoPayEnabled}&savePaymentMethod=\${savePaymentMethod}`,
    makeAchPayment: `${API_BASE_RESERVATIONS_URL}/v1/customer/\${customerId}/payment`,
    paymentHistory: `${BASE_URL}/services/stpsm-ws/customer-app/api/solrsearch/payments`,
    locationGatestList: `${API_BASE_URL}/api/equipments/locations?locationId=`,
    openGate: `${BASE_URL}/services/stpreservations-ws/v1/reservations/`,
    openGateForAdmin: `${BASE_URL}/services/stpreservations-ws/v1/`,
    locationGatestListSolr: `${BASE_URL}/services/stpsm-ws/customer-app/api/solrsearch/equipments`,
    deleteCard: `${API_BASE_RESERVATIONS_URL}/v1/payment-gateway/customers/\${customerId}/payment-Methods`,
  },
  getPdfDocument: `${API_BASE_URL}/api/v1/customer/`,
  createAgreement: `${API_BASE_URL}/api/v1/esign/agreement`,
  getAgreement: `${API_BASE_URL}/api/v1/esign/agreement/`,
  updateStatus: `${API_BASE_URL}/api/v1/esign/agreement/`,
  declineAgreement: `${API_BASE_URL}/api/v1/esign/customer/`,
  getEsignStatus: `${API_BASE_URL}/api/v1/esignStatus/customer/\${customerId}`,

  commonApis: {
    stateList: `https://c-services-qa4.copart.com/referencedata-ws/states/?isRefreshRequired=true`,
    phonevalidation: `https://c-services-qa4.copart.com/textmessaging-ws/v1/phonenumber`,
    postalCodeValidation: `https://c-services-qa4.copart.com/g2geoservices-ws/v2/address/validate/2/`,
    basicAuth: `https://c-reserve-dev4.safetruckparking.com/basicAuth`,
  },

  paymentInvoice: {
    invoices: `${API_BASE_URL}/customer-app/api/solrsearch/charges`,
    payments: `${API_BASE_URL}/customer-app/api/solrsearch/payments`,
    invoiceChargeDetails: `${API_BASE_RESERVATIONS_URL}/v1/bills/\${billNumber}/chargeDetails`,
    billpaymentHistory: `${API_BASE_RESERVATIONS_URL}/v1/bills/\${billNumber}/billpaymentsHistory`,
    paymentHistory: `${API_BASE_RESERVATIONS_URL}/v1/payments/\${billNumber}/billpaymentsHistory`,
    customerDetails: `${API_BASE_URL}/api/customers/\${customerId}`,
    locations: `${API_BASE_URL}/customer-app/api/solrsearch/locations`,
    invoiceDocumentUrl: `${API_BASE_RESERVATIONS_URL}/v1/invoiceReceipts/bill/\${billNumber}/invoice`,
    paymentDocumentUrl: `${API_BASE_RESERVATIONS_URL}/v1/invoiceReceipts/payment/\${billNumber}/receipt`,
  },
  appSettings: `${API_BASE_URL}/api/appSettings`,
};

export default apiConfig;
